import React, { useEffect, useState } from "react";
import { Swords, Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLATFORMS = [["instagram", "إنستغرام"], ["tiktok", "تيك توك"], ["snapchat", "سناب شات"], ["x", "X"]];
const EMPTY = { name: "", handle: "", platform: "instagram", followers: "", goal: "" };

export const Competitors = ({ projectId, onChange }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState([]);

  const load = () => api.get(`/social/competitors${projectId ? `?project_id=${projectId}` : ""}`).then((r) => { setItems(r.data); onChange?.(r.data); }).catch(() => {});
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const add = async (c = form) => {
    if (!c.name.trim()) return toast.error("اكتب اسم المنافس");
    setBusy(true);
    try {
      await api.post("/social/competitors", { ...c, project_id: projectId || null, followers: c.followers ? Number(c.followers) : null });
      setForm(EMPTY); setSuggested((s) => s.filter((x) => x.name !== c.name)); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => { try { await api.delete(`/social/competitors/${c.id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const suggest = async () => {
    if (!projectId) return toast.error("اختر مشروعاً أولاً ليقترح الذكاء الاصطناعي منافسيه");
    setSuggesting(true);
    try { const { data } = await api.post("/social/competitors/suggest", { project_id: projectId }); setSuggested(data.competitors || []); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  return (
    <div className="glass rounded-2xl p-5 border border-border" data-testid="competitors-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2"><Swords className="w-5 h-5 text-accent" /><h2 className="font-bold">المنافسون ({items.length})</h2></div>
        <Button size="sm" variant="outline" className="gap-1 border-accent/40 text-accent" onClick={suggest} disabled={suggesting} data-testid="suggest-competitors-btn">
          {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} اقتراح تلقائي
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3" data-testid="competitors-note">بيانات المنافسين الرقمية لا تتيحها المنصات، لذلك يحلّل الملخص التنفيذي أداء حملتك عبر قنواتك ويقترح أهم منافسيك حسب نشاطك في السعودية. أضف من تنافسه وهدفك (اختياري) لتُوجَّه التوصيات لدعم هدفك.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-4">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="اسم المنافس" className="bg-secondary border-border" data-testid="competitor-name" />
        <Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@الحساب" dir="ltr" className="bg-secondary border-border" data-optional="true" data-testid="competitor-handle" />
        <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
          <SelectTrigger className="bg-secondary border-border" data-testid="competitor-platform"><SelectValue /></SelectTrigger>
          <SelectContent>{PLATFORMS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min="0" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} placeholder="المتابعون (اختياري)" className="bg-secondary border-border" data-optional="true" data-testid="competitor-followers" />
        <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="هدفك في منافسته (اختياري)" className="bg-secondary border-border" data-optional="true" data-testid="competitor-goal" />
        <Button onClick={() => add()} disabled={busy} className="gap-1" data-testid="add-competitor-btn"><Plus className="w-4 h-4" /> إضافة</Button>
      </div>
      {suggested.length > 0 && (
        <div className="mb-4 rounded-xl border border-accent/30 bg-accent/5 p-3" data-testid="suggested-competitors">
          <div className="text-xs font-bold text-accent mb-2">اقتراحات الذكاء الاصطناعي — اضغط لإضافة:</div>
          <div className="flex flex-wrap gap-2">
            {suggested.map((s, i) => (
              <button key={i} onClick={() => add({ name: s.name, handle: s.handle || "", platform: s.platform || "instagram", followers: "", note: s.why || "" })}
                className="text-xs border border-border rounded-full px-3 py-1 hover:bg-primary/10 transition-colors" title={s.why} data-testid={`suggested-${i}`}>+ {s.name}</button>
            ))}
          </div>
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">أضف من تنافسه وهدفك (أو اطلب اقتراحاً تلقائياً) لتدعم توصيات الملخص التنفيذي توجهك.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((c) => (
            <div key={c.id} className="border border-border rounded-xl px-4 py-3 flex items-start justify-between gap-2" data-testid={`competitor-${c.id}`}>
              <div>
                <div className="font-bold text-sm">{c.name}</div>
                <div className="text-xs text-muted-foreground" dir="ltr">{c.handle || "—"} · {PLATFORMS.find(([k]) => k === c.platform)?.[1] || c.platform}</div>
                {c.followers ? <div className="text-xs mt-0.5">المتابعون: <b>{Number(c.followers).toLocaleString("en-US")}</b></div> : null}
                {c.note && <div className="text-[11px] text-muted-foreground mt-0.5">{c.note}</div>}
                {c.goal && <div className="text-[11px] text-accent mt-0.5" data-testid={`competitor-goal-${c.id}`}>الهدف: {c.goal}</div>}
              </div>
              <button onClick={() => remove(c)} className="text-destructive/70 hover:text-destructive" data-testid={`delete-competitor-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
